import { IonRippleEffect } from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import ImageCarousel from '../../../../components/ImageCarousel';
import WishlistIcon from '../../wish-list/wish-list-icon';
import { ProductType } from '../../../../types/product';
import { useHistory, useLocation } from 'react-router';
import useGlobalContext from '../../../../context/provider';
import { getCurrentUser } from '../../../../utils/getClientInfo';
import { IoTrashBinOutline } from 'react-icons/io5';
import { FaMinus } from 'react-icons/fa6';
import { IoMdAdd } from 'react-icons/io';
import {
  deleteCartItem,
  getAllCartItems,
  updateCartItem,
} from '../../../../context/actions/cart';
import ErrorToast from '../../../../components/error_toast';
import Spinner from '../../../../components/spinner';

const CartItem = ({
  product,
  customStyles,
}: {
  product: ProductType & {
    count: number;
    totalPrice: number;
    cartItemId: number;
  };
  customStyles?: string;
}) => {
  const { images, id, name, price, views, store } = product;
  const { cartDispatch, cartState } = useGlobalContext();
  const history = useHistory();
  const client = getCurrentUser();
  const [path, setPath] = useState<string>('');
  const [isVendorView, setIsVendorView] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [openError, setOpenError] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const location = useLocation();

  useEffect(() => {
    if (client.role === 'seller' && location.pathname.includes('dashboard')) {
      setPath(`/dashboard/items/detail?q=${id}&i=${name.slice(0, 30)}`);
    } else {
      setPath(`/${store?.name}/product/${name.slice(0, 30)}?id=${id}`);
    }
  }, []);

  useEffect(() => {
    if (cartState.cart) {
      setLoading(false);
    }
  }, [cartState]);

  const changeCount = async (add: boolean = false, minus: boolean = false) => {
    if (loading) {
      return;
    }
    let count = product.count;

    if (add) {
      count += 1;
    }
    if (minus) {
      if (count === 1) {
        return;
      }
      count -= 1;
    }

    setLoading(true);
    const res = await updateCartItem({ count, cartItemId: product.cartItemId });
    if (res.error) {
      setError(res.error);
      setOpenError(true);
    }
    await getAllCartItems()(cartDispatch);
  };

  const deleteItem = async () => {
    setLoading(true);
    const res = await deleteCartItem({ cartItemId: product.cartItemId });
    if (res.error) {
      setError(res.error);
      setOpenError(true);
    }
    await getAllCartItems()(cartDispatch);
  };

  return (
    <div
      className={`relative flex flex-col w-full cursor-pointer  h-[270px] md:h-[320px] overflow-hidden ion-activatable ripple-parent circles shadow-xs bg-white ${customStyles}`}
      onClick={() => {
        history.push({
          pathname: path,
          state: { id },
        });
      }}
    >
      <ErrorToast
        isOpen={openError}
        message={error}
        onClose={() => {
          setOpenError(false);
        }}
        type="error"
        position="bottom"
      />
      <IonRippleEffect />
      <div className={'relative w-full  flex items-center h-[60%] md:h-[65%]'}>
        <ImageCarousel data={images} name={name} />
        {!isVendorView && (
          <div
            className="absolute  items-center bottom-2 right-2 z-10 cursor-pointer "
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <WishlistIcon productId={id} />
          </div>
        )}
        {views?.length! > 4 && (
          <div className="absolute italic rounded-sm text-xs text-white p-1 capitalize font-bold items-center top-0 left-0 z-10 cursor-pointer bg-secondary ">
            Trending
          </div>
        )}
      </div>
      <div className={'m-0 px-2 py-2 h-[40%] md:h-[35%]'}>
        <div className="w-full h-auto flex items-center justify-between">
          <p className="text-base font-light capitalize whitespace-nowrap overflow-hidden text-ellipsis ">
            {name}
          </p>
        </div>

        <div className="w-full text-md font-medium h-auto ">
          UGX
          {price.toLocaleString('en-US', {
            maximumFractionDigits: 2,
          })}
        </div>

        <div className="w-full h-auto flex items-center justify-between pt-2">
          <div
            className="w-fit h-auto flex items-center "
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div
              className="rounded-2xl w-auto h-auto px-[5px] py-[5px] flex items-center justify-center cursor-pointer overflow-hidden relative ion-activatable ripple-parent circles"
              onClick={() => {
                changeCount(false, true);
              }}
            >
              <IonRippleEffect />
              <FaMinus size="100%" className="size-4 md:size-6 text-primary" />
            </div>
            <div
              className={
                'rounded-2xl h-auto mx-[4px] flex items-center justify-center border-2 border-primary cursor-pointer  text-xs md:text-base font-roboto font-semibold text-primary px-[6px] md:px-[0.8rem]'
              }
            >
              {product.count}
            </div>
            <div
              className="rounded-2xl  w-auto h-fit px-[5px] py-[5px] flex items-center justify-center cursor-pointer overflow-hidden relative ion-activatable ripple-parent circles"
              onClick={() => {
                changeCount(true, false);
              }}
            >
              <IonRippleEffect />
              <IoMdAdd size="100%" className="size-4 md:size-6 text-primary" />
            </div>
          </div>
          <div
            className="rounded-2xl w-auto h-fit p-[4%] md:p-2  flex items-center justify-center cursor-pointer overflow-hidden relative ion-activatable ripple-parent circles"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteItem();
            }}
          >
            <IonRippleEffect />
            <IoTrashBinOutline
              size="100%"
              className="size-4 md:size-6 text-secondary"
            />
          </div>
        </div>
      </div>
      {loading ? (
        <div
          className="absolute size-full z-10 flex items-center justify-center text-primary py-[4px] md:py-[3px] px-[2px] bg-[rgba(255,255,255,0.5)]"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Spinner type="lines-small" />
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default CartItem;
